import { openDayDate } from '../../utils/openDayDate';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Empty,
  Form,
  Input,
  Modal,
  Progress,
  Row,
  Space,
  Statistic,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  AppstoreOutlined,
  BranchesOutlined,
  ClockCircleOutlined,
  CrownOutlined,
  EditOutlined,
  HomeOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  TableOutlined,
  ThunderboltOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';
import { Trans, useTranslation } from 'react-i18next';
import i18n, { datePickerFormat, formatDate as formatLocaleDate, formatDateTime } from '../../i18n';
import MainLayout from '../../components/common/MainLayout';
import {
  useCloneSaveMutation,
  useGetCloneJobQuery,
  useGetSaveComparisonQuery,
  useMoveSaveCursorMutation,
  useRefreshSaveCapabilitiesMutation,
  useUpdateSaveMutation,
} from '../../api/savesV2Api';
import { useGetScenarioRewardsQuery } from '../../api/rewardsApi';
import ComparisonView from '../../components/scenarios/ComparisonView';
import StateCorrectionsPanel, { type CorrectionTarget } from '../../components/saves/StateCorrectionsPanel';
import SaveEventsPanel from '../../components/saves/SaveEventsPanel';
import { useRefreshWorld, useSave, useWorld, useWorldEntities } from '../../contexts/WorldContext';
// ScenarioPlaySurface удалён в Phase 4: scenario play живёт в обычных save-scoped
// страницах (Plantings/Plots/etc.) через WorldContext.
import type {
  CursorMoveEstimate,
  SaveWithProjection,
  ScenarioGoalEvaluation,
  ScenarioGoalSpec,
  ScenarioGoalProgress,
  ScenarioScoring,
  SaveWorldEntities,
} from '../../types/saves';

const { Title, Text, Paragraph } = Typography;

// Подписи: saves:shared.status.<status>
const STATUS_META: Record<string, { color: string }> = {
  active: { color: 'blue' },
  completed: { color: 'green' },
  archived: { color: 'default' },
  requires_migration: { color: 'red' },
};

const formatDate = (iso?: string | null, fallback = '—') =>
  iso ? formatDateTime(iso, 'short') : fallback;

const formatSimDate = (iso?: string | null, fallback = '—') =>
  iso ? formatLocaleDate(iso.slice(0, 10), 'short') : fallback;

const GOAL_OPERATOR_LABELS: Record<string, string> = {
  gte: '>=',
  gt: '>',
  lte: '<=',
  lt: '<',
  eq: '=',
};

const goalOperatorLabel = (operator?: string) => {
  const op = operator || 'gte';
  if (op === 'between') return i18n.t('saves:detail.goals.between');
  return GOAL_OPERATOR_LABELS[op] || op;
};

const formatGoalTarget = (target: ScenarioGoalEvaluation['target'], operator?: string) => {
  if (
    operator === 'between'
    && target
    && typeof target === 'object'
    && Number.isFinite(target.min)
    && Number.isFinite(target.max)
  ) {
    return `${target.min}–${target.max}%`;
  }
  return String(target);
};

const scoreTierFor = (scoring?: ScenarioScoring | null, passedCount = 0): string | null => {
  const tiers = Array.isArray(scoring?.tiers) ? scoring.tiers : [];
  const sorted = [...tiers].sort(
    (left, right) => Number(right.min_passed ?? right.minPassed ?? 0) - Number(left.min_passed ?? left.minPassed ?? 0),
  );
  const matched = sorted.find(
    (candidate) => passedCount >= Number(candidate.min_passed ?? candidate.minPassed ?? 0),
  );
  return matched?.tier != null ? String(matched.tier) : null;
};

interface DeepRewindState {
  estimate: CursorMoveEstimate;
  targetDate: string;
}

const formatEstimate = (estimate: CursorMoveEstimate) => (
  <Descriptions size="small" column={1} bordered>
    <Descriptions.Item label={i18n.t('saves:detail.estimate.ticks')}>
      {estimate.ticks_to_recalculate}
    </Descriptions.Item>
    <Descriptions.Item label={i18n.t('saves:detail.estimate.eventsAfterCursor')}>
      {estimate.events_after_cursor}
    </Descriptions.Item>
    <Descriptions.Item label={i18n.t('saves:detail.estimate.mayInvalidate')}>
      {estimate.invalidated_events_estimate}
    </Descriptions.Item>
    <Descriptions.Item label={i18n.t('saves:detail.estimate.replayTime')}>
      {i18n.t('saves:detail.estimate.ms', { value: estimate.estimated_replay_cost_ms })}
    </Descriptions.Item>
  </Descriptions>
);

const ScenarioBriefingCard: React.FC<{
  narrative?: Record<string, unknown> | null;
}> = ({ narrative }) => {
  const { t } = useTranslation('saves');
  const intro = typeof narrative?.intro === 'string' ? narrative.intro : null;
  const objective = typeof narrative?.objective === 'string' ? narrative.objective : null;
  const hint = typeof narrative?.hint === 'string' ? narrative.hint : null;

  if (!intro && !objective && !hint) return null;

  return (
    <Card title={t('detail.briefing.title')} size="small" style={{ marginBottom: 16 }}>
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        {intro ? <Text>{intro}</Text> : null}
        {objective ? <Text strong>{objective}</Text> : null}
        {hint ? <Alert type="info" showIcon message={t('detail.briefing.hint')} description={hint} /> : null}
      </Space>
    </Card>
  );
};

const ScenarioGoalsCard: React.FC<{
  progress: ScenarioGoalProgress | null | undefined;
  cursorSimDate?: string | null;
  templateGoals?: ScenarioGoalSpec[] | null;
  templateScoring?: ScenarioScoring | null;
}> = ({
  progress,
  cursorSimDate,
  templateGoals,
  templateScoring,
}) => {
  const { t } = useTranslation('saves');
  const progressGoals = progress?.goals ?? [];
  const progressGoalsById = new Map(progressGoals.map((goal) => [goal.id, goal]));
  const templateGoalIds = new Set((templateGoals ?? []).map((goal) => goal.id));
  const displayGoals = (templateGoals?.length
    ? [
        ...templateGoals.map((goal) => {
          const progressGoal = progressGoalsById.get(goal.id);
          return {
            id: goal.id,
            label: progressGoal?.label || goal.label,
            metric: progressGoal?.metric || goal.metric,
            operator: progressGoal?.operator || goal.operator,
            target: progressGoal?.target ?? goal.target,
            value: progressGoal?.value,
            passed: progressGoal?.passed,
          } satisfies ScenarioGoalEvaluation;
        }),
        ...progressGoals.filter((goal) => !templateGoalIds.has(goal.id)),
      ]
    : progressGoals.length > 0
      ? progressGoals
      : []).filter(Boolean);

  if (!progress) {
    return (
      <Card title={t('detail.goals.title')} size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type="info"
            showIcon
            message={t('detail.goals.notComputed')}
            description={displayGoals.length > 0
              ? (cursorSimDate
                ? t('detail.goals.pendingWithGoalsAtDate', { date: formatSimDate(cursorSimDate) })
                : t('detail.goals.pendingWithGoals'))
              : (cursorSimDate
                ? t('detail.goals.pendingAtDate', { date: formatSimDate(cursorSimDate) })
                : t('detail.goals.pending'))}
          />
          {displayGoals.length > 0 ? (
            <div>
              {displayGoals.map((goal) => (
                <div key={goal.id} style={{ marginBottom: 4 }}>
                  <Tag color="blue">…</Tag>
                  <Text strong>{goal.label || goal.id}</Text>
                  <Text type="secondary"> — {goal.metric}</Text>
                  <Text type="secondary"> {goalOperatorLabel(goal.operator)} </Text>
                  <Text>{formatGoalTarget(goal.target, goal.operator)}</Text>
                </div>
              ))}
            </div>
          ) : null}
        </Space>
      </Card>
    );
  }

  const total = displayGoals.length;
  const passed = displayGoals.filter((goal) => goal.passed).length;
  const hasGoals = displayGoals.length > 0;
  const hasMeasuredGoals = Boolean(displayGoals.some((goal) => goal.value !== undefined && goal.value !== null));
  const derivedScoreTier = scoreTierFor(templateScoring, passed);
  const effectiveScoreTier = hasMeasuredGoals || progress.completed
    ? (derivedScoreTier ?? (progress.scoreTier != null ? String(progress.scoreTier) : null))
    : null;
  const percent = total > 0 ? Math.round((passed / total) * 100) : 0;

  if (!hasGoals) {
    return (
      <Card title={t('detail.goals.title')} size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type="warning"
            showIcon
            message={t('detail.goals.undefinedTitle')}
            description={t('detail.goals.undefinedDescription')}
          />
          {progress.scoreTier ? (
            <Alert
              type="info"
              showIcon
              message={t('detail.goals.currentRewardTier', { tier: String(progress.scoreTier) })}
              description={t('detail.goals.rewardTierUnexplained')}
            />
          ) : null}
        </Space>
      </Card>
    );
  }

  return (
    <Card title={t('detail.goals.title')} size="small" style={{ marginBottom: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Space>
          <Statistic title={t('detail.goals.passed')} value={passed} suffix={`/ ${total}`} />
          {effectiveScoreTier ? (
            <Statistic
              title={t('detail.goals.currentTier')}
              valueRender={() => <Tag color="gold">Tier {effectiveScoreTier}</Tag>}
              value={0}
            />
          ) : null}
          {progress.completed ? <Tag color="green">{t('detail.goals.allReached')}</Tag> : null}
        </Space>
        <Progress percent={percent} status={progress.completed ? 'success' : 'active'} />
        <Alert
          type="info"
          showIcon
          message={effectiveScoreTier
            ? t('detail.goals.tierNow', { tier: effectiveScoreTier })
            : t('detail.goals.tierLater')}
          description={hasMeasuredGoals
            ? t('detail.goals.tierExplained')
            : t('detail.goals.tierNotYet')}
        />
        {displayGoals.length ? (
          <div>
            {displayGoals.map((goal: ScenarioGoalEvaluation) => (
              <div key={goal.id} style={{ marginBottom: 4 }}>
                <Tag color={goal.passed ? 'green' : goal.value !== undefined && goal.value !== null ? 'default' : 'blue'}>
                  {goal.passed ? '✓' : goal.value !== undefined && goal.value !== null ? '·' : '…'}
                </Tag>
                <Text strong>{goal.label || goal.id}</Text>
                <Text type="secondary"> — {goal.metric}</Text>
                <Text type="secondary"> {goalOperatorLabel(goal.operator)} </Text>
                <Text>{formatGoalTarget(goal.target, goal.operator)}</Text>
                {goal.value !== undefined && goal.value !== null ? (
                  <Text type="secondary"> {t('detail.goals.currentValue', { value: String(goal.value) })}</Text>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </Space>
    </Card>
  );
};

const TIER_COLORS: Record<string, string> = { gold: 'gold', silver: 'default', bronze: 'volcano' };

const ScenarioRewardsCard: React.FC<{ saveId: string }> = ({ saveId }) => {
  const { t } = useTranslation('saves');
  const { data: rewards = [], isLoading } = useGetScenarioRewardsQuery({ saveId });
  if (!isLoading && rewards.length === 0) return null;
  return (
    <Card title={t('detail.rewards.title')} size="small" style={{ marginBottom: 16 }} loading={isLoading}>
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        {rewards.map((r) => (
          <Space key={r.id} wrap size={8}>
            <Tag color={TIER_COLORS[String(r.reward_type)] || 'blue'}>{r.reward_type}</Tag>
            {r.payload?.scoreTier && <Text type="secondary">tier: {r.payload.scoreTier}</Text>}
            {r.payload?.passedCount != null && r.payload?.total != null && (
              <Text type="secondary">{t('detail.rewards.goals', { passed: r.payload.passedCount, total: r.payload.total })}</Text>
            )}
            <Text type="secondary" style={{ fontSize: 12 }}>{formatDateTime(r.created_at)}</Text>
          </Space>
        ))}
      </Space>
    </Card>
  );
};

const RuntimeCard: React.FC<{ runtime: unknown }> = ({ runtime }) => (
  <Card title="Runtime snapshot" size="small" style={{ marginBottom: 16 }}>
    <pre
      style={{
        maxHeight: 280,
        overflow: 'auto',
        background: '#fafafa',
        padding: 12,
        borderRadius: 4,
        fontSize: 12,
      }}
    >
      {JSON.stringify(runtime, null, 2)}
    </pre>
  </Card>
);

const SaveWorldAccessCard: React.FC<{
  saveId: string;
  entities: SaveWorldEntities;
}> = ({ saveId, entities }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('saves');
  const fields = entities.fields || [];
  const plots = entities.plots || [];
  const beds = entities.beds || [];
  const plantings = entities.plantings || [];
  const firstField = fields[0];
  const firstPlot = plots[0];
  const firstBed = beds[0];
  const planPath = firstField
    ? `/saves/${saveId}/fields/${firstField.id}/plan`
    : firstPlot
      ? `/saves/${saveId}/plots/${firstPlot.id}/plan`
      : null;

  return (
    <Card
      size="small"
      title={
        <Space>
          <AppstoreOutlined /> {t('detail.world.title')}
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Statistic title={t('detail.world.fields')} value={fields.length} />
        </Col>
        <Col xs={12} md={6}>
          <Statistic title={t('detail.world.plots')} value={plots.length} />
        </Col>
        <Col xs={12} md={6}>
          <Statistic title={t('detail.world.beds')} value={beds.length} />
        </Col>
        <Col xs={12} md={6}>
          <Statistic title={t('detail.world.plantings')} value={plantings.length} />
        </Col>
      </Row>

      <Space wrap style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<TableOutlined />}
          disabled={!planPath}
          onClick={() => planPath && navigate(planPath)}
        >
          {t('detail.world.openGrid')}
        </Button>
        <Button icon={<HomeOutlined />} onClick={() => navigate(`/saves/${saveId}/plantings`)}>
          {t('detail.world.plantings')}
        </Button>
        <Button
          icon={<AppstoreOutlined />}
          disabled={!firstBed}
          onClick={() => firstBed && navigate(`/saves/${saveId}/beds/${firstBed.id}`)}
        >
          {t('detail.world.beds')}
        </Button>
        <Button icon={<ThunderboltOutlined />} onClick={() => navigate(`/saves/${saveId}/simulation`)}>
          {t('detail.world.simulation')}
        </Button>
      </Space>

      {fields.length || plots.length ? (
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          {fields.slice(0, 6).map((field) => {
            const fieldPlots = plots.filter((plot) => plot.fieldId === field.id);
            return (
              <div
                key={field.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  alignItems: 'center',
                  borderTop: '1px solid #f0f0f0',
                  paddingTop: 8,
                }}
              >
                <Space wrap>
                  <Text strong>{field.name || t('detail.world.fieldFallback')}</Text>
                  <Badge count={fieldPlots.length} overflowCount={999} />
                  <Text type="secondary">{t('detail.world.plotsCount', { count: fieldPlots.length })}</Text>
                </Space>
                <Space wrap>
                  <Button size="small" onClick={() => navigate(`/saves/${saveId}/fields/${field.id}/info`)}>
                    {t('detail.world.info')}
                  </Button>
                  <Button size="small" type="link" onClick={() => navigate(`/saves/${saveId}/fields/${field.id}/plan`)}>
                    {t('detail.world.plan')}
                  </Button>
                </Space>
              </div>
            );
          })}
          {!fields.length
            ? plots.slice(0, 6).map((plot) => (
                <div
                  key={plot.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    alignItems: 'center',
                    borderTop: '1px solid #f0f0f0',
                    paddingTop: 8,
                  }}
                >
                  <Text strong>{plot.name || t('detail.world.plotFallback')}</Text>
                  <Space wrap>
                    <Button size="small" onClick={() => navigate(`/saves/${saveId}/plots/${plot.id}/info`)}>
                      {t('detail.world.info')}
                    </Button>
                    <Button size="small" type="link" onClick={() => navigate(`/saves/${saveId}/plots/${plot.id}/plan`)}>
                      {t('detail.world.plan')}
                    </Button>
                  </Space>
                </div>
              ))
            : null}
        </Space>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('detail.world.empty')}
        />
      )}
    </Card>
  );
};

const CapabilitiesCard: React.FC<{
  save: SaveWithProjection;
  onRefresh: () => void;
  refreshing: boolean;
}> = ({ save, onRefresh, refreshing }) => {
  const { t } = useTranslation('saves');
  if (save.type !== 'free') return null;
  const snapshot = save.capabilitiesSnapshot;
  return (
    <Card
      size="small"
      title={
        <Space>
          <SafetyCertificateOutlined /> {t('detail.capabilities.title')}
        </Space>
      }
      extra={
        <Tooltip title={t('detail.capabilities.refreshTooltip')}>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            loading={refreshing}
            onClick={onRefresh}
          >
            {t('common:actions.refresh')}
          </Button>
        </Tooltip>
      }
      style={{ marginBottom: 16 }}
    >
      {snapshot ? (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {snapshot.tier ? (
            <div>
              <CrownOutlined /> <Text strong>Tier:</Text> <Tag color="purple">{snapshot.tier}</Tag>
            </div>
          ) : null}
          {Array.isArray(snapshot.features) && snapshot.features.length > 0 ? (
            <div>
              <Text type="secondary">{t('detail.capabilities.features')} </Text>
              <Space wrap size={[4, 4]}>
                {snapshot.features.map((f) => (
                  <Tag key={f}>{f}</Tag>
                ))}
              </Space>
            </div>
          ) : null}
          <pre
            style={{
              maxHeight: 200,
              overflow: 'auto',
              background: '#fafafa',
              padding: 8,
              borderRadius: 4,
              fontSize: 11,
            }}
          >
            {JSON.stringify(snapshot, null, 2)}
          </pre>
        </Space>
      ) : (
        <Empty description={t('detail.capabilities.empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
    </Card>
  );
};

export default function SaveDetailPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('saves');
  const { saveId } = useParams<{ saveId: string }>();
  const save = useSave();
  const world = useWorld();
  const worldEntities = useWorldEntities();
  const correctionTargets = useMemo<CorrectionTarget[]>(() => {
    const beds = (worldEntities.beds || []).map((b) => ({
      kind: 'bed' as const, id: b.id, label: b.name || t('detail.bedFallback', { id: b.id.slice(0, 8) }),
    }));
    const plantings = (worldEntities.plantings || [])
      .filter((p) => p.lifecycleStatus !== 'dead' && !p.inStorage)
      .map((p) => {
        const bed = (worldEntities.beds || []).find((b) => b.id === p.bedId);
        return {
          kind: 'planting' as const, id: p.id,
          label: bed?.name
            ? t('detail.plantingOnBed', { crop: p.crop || t('detail.plantingFallback'), bed: bed.name })
            : (p.crop || t('detail.plantingFallback')),
        };
      });
    return [...plantings, ...beds];
  }, [worldEntities.beds, worldEntities.plantings, t]);
  const refreshWorld = useRefreshWorld();
  const [cursorTarget, setCursorTarget] = useState<Dayjs | null>(null);
  const [cloneTarget, setCloneTarget] = useState<Dayjs | null>(null);
  const [activeCloneJobId, setActiveCloneJobId] = useState<string | null>(null);
  const [deepRewind, setDeepRewind] = useState<DeepRewindState | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsForm] = Form.useForm();

  const isScenario = save?.type === 'scenario';
  const {
    data: comparisonData,
    isLoading: comparisonLoading,
    isError: comparisonError,
  } = useGetSaveComparisonQuery(saveId || '', { skip: !saveId || !isScenario });
  const [moveCursor, { isLoading: moving }] = useMoveSaveCursorMutation();
  const [cloneSave, { isLoading: cloneEnqueueing }] = useCloneSaveMutation();
  // Polling on the active clone job — RTK Query refetch every 1.5s while a
  // job is in flight. Stops automatically once status is terminal (or
  // activeCloneJobId is reset to null after we navigate).
  const cloneJobQuery = useGetCloneJobQuery(
    { saveId: saveId || '', jobId: activeCloneJobId || '' },
    {
      skip: !saveId || !activeCloneJobId,
      pollingInterval: activeCloneJobId ? 1500 : 0,
      refetchOnMountOrArgChange: true,
    }
  );
  const cloneJob = cloneJobQuery.data;
  const cloning = cloneEnqueueing
    || (Boolean(activeCloneJobId)
      && (!cloneJob || cloneJob.status === 'queued' || cloneJob.status === 'running'));
  const [refreshCapabilities, { isLoading: refreshing }] = useRefreshSaveCapabilitiesMutation();
  const [updateSave, { isLoading: savingSettings }] = useUpdateSaveMutation();

  const canEditSettings = world?.farm?.myRole === 'owner'
    || world?.farm?.myRole === 'manager'
    || Boolean(world?.farm?.isOwner);

  const openSettings = () => {
    settingsForm.setFieldsValue({
      name: world?.farm?.name ?? save?.farmName ?? '',
      locationCoords: world?.farm?.locationCoords ?? '',
      description: world?.farm?.description ?? '',
    });
    setSettingsOpen(true);
  };

  const handleSaveSettings = async () => {
    if (!saveId) return;
    try {
      const values = await settingsForm.validateFields();
      await updateSave({
        saveId,
        name: typeof values.name === 'string' ? values.name.trim() : undefined,
        locationCoords: values.locationCoords ? String(values.locationCoords).trim() : null,
        description: values.description ? String(values.description).trim() : null,
      }).unwrap();
      message.success(t('detail.settings.saved'));
      setSettingsOpen(false);
      refreshWorld();
    } catch (err: any) {
      if (err?.errorFields) return; // validation error — fields already highlighted
      message.error(err?.data?.message || t('detail.settings.saveFailed'));
    }
  };

  // Drive UI off the polled clone job. Terminal states (succeeded/failed/
  // cancelled) clear the active job and either navigate to the new save
  // or surface the error.
  useEffect(() => {
    if (!cloneJob || !activeCloneJobId) return;
    if (cloneJob.status === 'succeeded' && cloneJob.targetSaveId) {
      message.destroy('clone-job');
      message.success(t('detail.clone.ready'));
      const targetId = cloneJob.targetSaveId;
      setActiveCloneJobId(null);
      navigate(`/saves/${targetId}`);
    } else if (cloneJob.status === 'failed') {
      message.destroy('clone-job');
      message.error(t('detail.clone.failed', { error: cloneJob.error || t('detail.clone.unknownError') }));
      setActiveCloneJobId(null);
    } else if (cloneJob.status === 'cancelled') {
      message.destroy('clone-job');
      message.warning(t('detail.clone.cancelled'));
      setActiveCloneJobId(null);
    }
  }, [cloneJob, activeCloneJobId, navigate, t]);

  if (!saveId) {
    return (
      <MainLayout breadcrumb={<Breadcrumb items={[{ title: t('shared.breadcrumbSave') }]} />}>
        <Alert type="error" showIcon message={t('detail.noSaveId')} />
      </MainLayout>
    );
  }

  const statusMeta = STATUS_META[save.status]
    ? { ...STATUS_META[save.status], label: t(`shared.status.${save.status}`) }
    : { label: save.status, color: 'default' };
  const cursorSimDate = save.cursor?.simDate || save.cursor?.sim_date || null;
  const goalProgress = world.runtime?.goalsProgress || null;
  const templateGoals = comparisonData?.scenarioTemplate?.goals ?? null;
  const templateScoring = comparisonData?.scenarioTemplate?.scoring ?? null;
  const templateNarrative = comparisonData?.scenarioTemplate?.narrative ?? null;

  const performMove = async (date: string, confirmDeep = false) => {
    try {
      const body = {
        sim_date: date,
        expected_save_revision: save.revision,
        ...(confirmDeep ? { confirm_deep_rewind: true } : {}),
      };
      const result = await moveCursor({ saveId, body }).unwrap();
      if ((result as { status?: string }).status === 'requires_confirmation') {
        const r = result as Extract<typeof result, { status: 'requires_confirmation' }>;
        setDeepRewind({ estimate: r.estimate, targetDate: date });
        return;
      }
      message.success(t('detail.cursor.moved'));
      setCursorTarget(null);
      setDeepRewind(null);
      await refreshWorld();
    } catch (err) {
      // 409 deep rewind comes through as error
      const e = err as { data?: { message?: string; estimate?: CursorMoveEstimate; reason?: string } };
      if (e?.data?.estimate && e.data.reason === 'deep_rewind') {
        setDeepRewind({ estimate: e.data.estimate, targetDate: date });
        return;
      }
      message.error(e?.data?.message || t('detail.cursor.moveFailed'));
    }
  };

  const handleMoveCursor = () => {
    if (!cursorTarget) {
      message.warning(t('detail.cursor.selectDate'));
      return;
    }
    const iso = cursorTarget.format('YYYY-MM-DD');
    performMove(iso, false);
  };

  const handleConfirmDeepRewind = async () => {
    if (!deepRewind) return;
    await performMove(deepRewind.targetDate, true);
  };

  const enqueueClone = async (iso: string): Promise<boolean> => {
    try {
      const enqueued = await cloneSave({
        saveId,
        body: { sim_date: iso, expected_save_revision: save.revision },
      }).unwrap();
      setActiveCloneJobId(enqueued.cloneJobId);
      message.loading({
        content: t('detail.clone.queued', { job: enqueued.cloneJobId.slice(0, 8) }),
        key: 'clone-job',
        duration: 0,
      });
      return true;
    } catch (err) {
      const e = err as { data?: { message?: string } };
      message.error(e?.data?.message || t('detail.clone.enqueueFailed'));
      return false;
    }
  };

  const handleCloneFromDeepRewind = async () => {
    if (!deepRewind) return;
    const ok = await enqueueClone(deepRewind.targetDate);
    if (ok) setDeepRewind(null);
  };

  const handleCloneSave = () => {
    if (!cloneTarget) {
      message.warning(t('detail.clone.selectDate'));
      return;
    }
    const iso = cloneTarget.format('YYYY-MM-DD');
    Modal.confirm({
      title: t('detail.clone.confirmTitle'),
      content: (
        <Space direction="vertical">
          <Text>
            <Trans
              t={t}
              i18nKey="detail.clone.confirmBody"
              values={{ date: formatSimDate(iso) }}
              components={{ b: <Text strong /> }}
            />
          </Text>
          <Text type="secondary">{t('detail.clone.confirmEvents')}</Text>
        </Space>
      ),
      okText: t('detail.clone.confirmOk'),
      cancelText: t('common:actions.cancel'),
      onOk: async () => {
        const ok = await enqueueClone(iso);
        if (ok) setCloneTarget(null);
      },
    });
  };

  const handleRefreshCapabilities = () => {
    Modal.confirm({
      title: t('detail.capabilities.confirmTitle'),
      content: (
        <Space direction="vertical">
          <Text>
            <Trans
              t={t}
              i18nKey="detail.capabilities.confirmBody"
              components={{ code: <Text code /> }}
            />
          </Text>
          <Text type="secondary">{t('detail.capabilities.confirmSafe')}</Text>
        </Space>
      ),
      okText: t('common:actions.refresh'),
      cancelText: t('common:actions.cancel'),
      onOk: async () => {
        try {
          const result = await refreshCapabilities(saveId).unwrap();
          message.success(
            t('detail.capabilities.refreshed', { count: result.invalidated_events })
          );
        } catch (err) {
          const e = err as { data?: { message?: string } };
          message.error(e?.data?.message || t('detail.capabilities.refreshFailed'));
        }
      },
    });
  };

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: <Link to="/">{t('shared.breadcrumbHome')}</Link> },
            { title: <Link to="/saves">{t('shared.breadcrumbSaves')}</Link> },
            { title: saveId.slice(0, 8) },
          ]}
        />
      }
    >
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <Title level={3} style={{ margin: 0 }}>
          {save.type === 'free' ? t('shared.type.free') : t('shared.type.scenario')}{' '}
          <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
        </Title>
        <Space wrap>
          {canEditSettings ? (
            <Button icon={<EditOutlined />} onClick={openSettings}>
              {t('detail.settings.title')}
            </Button>
          ) : null}
          <Button onClick={() => refreshWorld()}>{t('common:actions.refresh')}</Button>
        </Space>
      </Space>

      <SaveWorldAccessCard saveId={saveId} entities={worldEntities} />

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title={t('detail.meta.title')} size="small" style={{ marginBottom: 16 }}>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="ID">
                <Text code copyable>{save.id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('common:labels.type')}>
                <Tag color={save.type === 'free' ? 'green' : 'orange'}>{save.type}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('common:labels.status')}>
                <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Engine version">{save.engineVersion}</Descriptions.Item>
              <Descriptions.Item label="Revision">{String(save.revision)}</Descriptions.Item>
              <Descriptions.Item label="Cache policy">{save.cachePolicy}</Descriptions.Item>
              {save.farmId ? (
                <Descriptions.Item label={t('shared.containerFarm')}>
                  {save.farmName ? <Text>{save.farmName} </Text> : null}
                  <Text code copyable>{save.farmId}</Text>
                </Descriptions.Item>
              ) : null}
              {save.scenarioTemplateId ? (
                <Descriptions.Item label="Scenario template">
                  <Text code copyable>{save.scenarioTemplateId}</Text>
                  {save.scenarioTemplateVersion != null ? (
                    <Text type="secondary"> v{save.scenarioTemplateVersion}</Text>
                  ) : null}
                </Descriptions.Item>
              ) : null}
              <Descriptions.Item label={t('shared.createdAt')}>{formatDate(save.createdAt)}</Descriptions.Item>
              <Descriptions.Item label={t('detail.meta.updatedAt')}>{formatDate(save.updatedAt)}</Descriptions.Item>
              <Descriptions.Item label={t('shared.lastPlayed')}>
                {formatDate(save.lastPlayedAt, t('shared.neverPlayed'))}
              </Descriptions.Item>
              {save.completedAt ? (
                <Descriptions.Item label={t('detail.meta.completedAt')}>
                  {formatDate(save.completedAt)}
                </Descriptions.Item>
              ) : null}
            </Descriptions>
          </Card>

          <Card
            size="small"
            title={
              <Space>
                <ClockCircleOutlined /> {t('detail.cursor.title')}
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
              <Descriptions.Item label={save.type === 'free' ? t('detail.cursor.lastCompletedDay') : t('detail.cursor.scenarioDate')}>
                <Text strong>{formatSimDate(cursorSimDate)}</Text>
              </Descriptions.Item>
              {save.type === 'free' && <Descriptions.Item label={t('detail.cursor.openDay')}>
                <Text strong>{formatSimDate(openDayDate(cursorSimDate, save.type))}</Text>
              </Descriptions.Item>}
              <Descriptions.Item label="Cursor mode">
                {save.cursor?.mode || save.cursor?.cursor_mode || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Timezone">
                {save.cursor?.timezone || 'UTC'}
              </Descriptions.Item>
              <Descriptions.Item label="Auto-advance">
                {save.cursor?.autoAdvanceEnabled ?? save.cursor?.auto_advance_enabled ? t('common:actions.yes') : t('common:actions.no')}
              </Descriptions.Item>
            </Descriptions>

            <Space wrap>
              <DatePicker
                placeholder={t('detail.cursor.lastCompletedDay')}
                value={cursorTarget}
                format={datePickerFormat()}
                // Anchor the picker to the SIM cursor, not real "today", so a
                // rewind to "May" stays in the simulation's year instead of the
                // real-calendar year (off-by-a-year cursor desync).
                defaultPickerValue={cursorSimDate ? dayjs(cursorSimDate) : undefined}
                onChange={setCursorTarget}
              />
              <Button
                type="primary"
                onClick={handleMoveCursor}
                loading={moving}
                disabled={save.status !== 'active'}
              >
                {t('detail.cursor.move')}
              </Button>
            </Space>
          </Card>

          <Card
            size="small"
            title={
              <Space>
                <BranchesOutlined /> {t('detail.branch.title')}
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            <Paragraph type="secondary" style={{ marginBottom: 8 }}>
              {t('detail.branch.description')}
            </Paragraph>
            <Space wrap>
              <DatePicker
                placeholder={t('detail.branch.datePlaceholder')}
                value={cloneTarget}
                format={datePickerFormat()}
                onChange={setCloneTarget}
              />
              <Button onClick={handleCloneSave} loading={cloning}>
                {t('detail.branch.clone')}
              </Button>
            </Space>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          {save.type === 'scenario' ? (
            <ScenarioBriefingCard narrative={templateNarrative} />
          ) : null}
          {save.type === 'scenario' ? (
            <ScenarioGoalsCard
              progress={goalProgress}
              cursorSimDate={cursorSimDate}
              templateGoals={templateGoals}
              templateScoring={templateScoring}
            />
          ) : null}
          {save.type === 'scenario' && saveId ? (
            <ScenarioRewardsCard saveId={saveId} />
          ) : null}
          {save.type === 'scenario' ? (
            <ComparisonView
              data={comparisonData}
              isLoading={comparisonLoading}
              isError={comparisonError}
            />
          ) : null}
          <CapabilitiesCard
            save={save}
            onRefresh={handleRefreshCapabilities}
            refreshing={refreshing}
          />
          <StateCorrectionsPanel
            saveId={save.id}
            sessionId={save.simSessionId || null}
            cursorDate={world.cursor?.simDate || null}
            targets={correctionTargets}
          />
          <SaveEventsPanel save={save} />
          <RuntimeCard runtime={world.runtime} />
        </Col>
      </Row>

      <Modal
        open={!!deepRewind}
        title={
          <Space>
            <WarningOutlined style={{ color: '#faad14' }} />
            {t('detail.deepRewind.title')}
          </Space>
        }
        onCancel={() => setDeepRewind(null)}
        footer={[
          <Button key="cancel" onClick={() => setDeepRewind(null)}>
            {t('common:actions.cancel')}
          </Button>,
          <Button
            key="clone"
            icon={<BranchesOutlined />}
            onClick={handleCloneFromDeepRewind}
            loading={cloning}
          >
            {t('detail.deepRewind.cloneInstead')}
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            onClick={handleConfirmDeepRewind}
            loading={moving}
          >
            {t('detail.deepRewind.confirm')}
          </Button>,
        ]}
      >
        {deepRewind ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Paragraph>
              <Trans
                t={t}
                i18nKey="detail.deepRewind.warning"
                values={{ date: formatSimDate(deepRewind.targetDate) }}
                components={{ b: <Text strong /> }}
              />
            </Paragraph>
            <Paragraph strong>
              {t('common:messages.rewindRemoves', { date: formatSimDate(deepRewind.targetDate) })}
            </Paragraph>
            <Paragraph type="secondary">
              <Trans t={t} i18nKey="detail.deepRewind.branchAdvice" components={{ b: <Text strong /> }} />
            </Paragraph>
            {formatEstimate(deepRewind.estimate)}
          </Space>
        ) : null}
      </Modal>

      <Modal
        title={t('detail.settings.title')}
        open={settingsOpen}
        onOk={handleSaveSettings}
        onCancel={() => setSettingsOpen(false)}
        okText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
        confirmLoading={savingSettings}
        destroyOnClose
      >
        <Form form={settingsForm} layout="vertical" preserve={false}>
          <Form.Item
            name="name"
            label={t('detail.settings.name')}
            rules={[{ required: true, whitespace: true, message: t('detail.settings.nameRequired') }]}
          >
            <Input placeholder={t('detail.settings.namePlaceholder')} maxLength={120} />
          </Form.Item>
          <Form.Item
            name="locationCoords"
            label={t('detail.settings.coords')}
            tooltip={t('detail.settings.coordsTooltip')}
          >
            <Input placeholder={t('detail.settings.coordsPlaceholder')} allowClear />
          </Form.Item>
          <Form.Item name="description" label={t('common:labels.description')}>
            <Input.TextArea rows={3} placeholder={t('detail.settings.descriptionPlaceholder')} allowClear />
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
}
